import React from "react";
import { ValueFormatter, ValueType } from "src/utils/formatters";
import {
  attributeToTitle,
  formatAggregationFieldLabel,
  formatTitle,
} from "src/utils/strings";
import { isEmptyValue, TableFormatter } from "../utils/table-utils";
import { formatDuration } from "src/utils/duration";
import { asKey } from "src/utils/fields";
import styles from "./Tooltip.module.css";
import { TooltipContext, TooltipContentModule } from "./tooltip-types";

function formatTooltipLabel(key: string, context?: TooltipContext): string {
  // If we have aggregations, try to find the matching one for better formatting
  if (context?.aggregations?.length) {
    const aggregation = context.aggregations.find(
      (agg: any) => agg.key === key || agg.field === key
    );
    if (aggregation) {
      return TableFormatter.formatAggregationTitle(aggregation);
    }
  }

  // Try to get field metadata for better labels
  const field = context?.groupBys?.find((f: any) => asKey(f) === key);
  if (field) {
    return attributeToTitle(field.attribute);
  }

  // Use the shared formatting function for consistent labels
  return formatTitle(key);
}

function formatTooltipValue(
  value: unknown,
  field?: string,
  context?: TooltipContext
): string {
  if (isEmptyValue(value)) return "-";

  // Find the aggregation config if available
  const aggregation = context?.aggregations?.find(
    (agg: any) => agg.field === field
  );

  // Handle objects with name/value properties
  if (typeof value === "object" && value !== null) {
    const obj = value as any;
    if ("value" in obj) {
      // If we have an aggregation, use its type for formatting
      if (aggregation) {
        const detectedType = ValueFormatter.detectValueType({
          field: aggregation.field,
        }) as ValueType | undefined;
        return ValueFormatter.format(Number(obj.value), {
          field: aggregation.field,
          type: detectedType,
          style: "human",
        });
      }
      return ValueFormatter.format(Number(obj.value), { field });
    }
    if ("name" in obj) {
      return attributeToTitle(String(obj.name));
    }
    return JSON.stringify(value);
  }

  // Handle numeric values
  if (typeof value === "number" || !Number.isNaN(Number(value))) {
    // Check if this is a duration field
    if (
      field?.toLowerCase().includes("duration") ||
      field?.toLowerCase().includes("time")
    ) {
      return formatDuration(Number(value));
    }

    // If we have an aggregation, use its type for formatting
    if (aggregation) {
      const detectedType = ValueFormatter.detectValueType({
        field: aggregation.field,
      }) as ValueType | undefined;
      return ValueFormatter.format(Number(value), {
        field: aggregation.field,
        type: detectedType,
        style: "human",
      });
    }

    return ValueFormatter.format(Number(value), { field });
  }

  // For string values, try to make them more readable
  return attributeToTitle(String(value));
}

type PivotStatsResult =
  | {
      type: "aggregation";
      measureKey: string;
      stats: Record<string, unknown>;
    }
  | {
      type: "pivot";
      measureKey: string;
      currentValue: unknown;
      distribution: Array<{
        key: string;
        value: unknown;
        percentage: number;
      }>;
    }
  | null;

function calculatePivotStats(context: TooltipContext): PivotStatsResult {
  const { record, currentColumn } = context;

  // Early boundary checks
  if (!currentColumn?.measureKey || !record?.measures) {
    return null;
  }

  const currentValue = record.measures[currentColumn.measureKey];
  if (!currentValue) {
    return null;
  }

  // Handle top-level aggregation cell
  if (typeof currentValue === "object" && !Array.isArray(currentValue)) {
    const stats = Object.entries(currentValue as Record<string, unknown>)
      .filter(([_, value]) => value != null)
      .reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value,
        }),
        {} as Record<string, unknown>
      );

    return {
      type: "aggregation",
      measureKey: currentColumn.measureKey,
      stats,
    };
  }

  // Handle regular pivot cell
  const measureValues = Object.entries(record.measures)
    .filter(([_, value]) => value != null)
    .map(([key, value]) => ({
      key,
      value: typeof value === "object" ? (value as any).value ?? value : value,
    }));

  if (measureValues.length === 0) {
    return null;
  }

  const total = measureValues.reduce(
    (sum, item) => sum + Number(item.value),
    0
  );

  return {
    type: "pivot",
    measureKey: currentColumn.measureKey,
    currentValue,
    distribution: measureValues.map((item) => ({
      key: item.key,
      value: item.value,
      percentage: total ? (Number(item.value) / total) * 100 : 0,
    })),
  };
}

export const PivotTooltipModule: TooltipContentModule = {
  type: "pivot",
  priority: 2,
  shouldRender: (context: TooltipContext) => context.type === "pivot",
  render: (context: TooltipContext) => {
    const stats = calculatePivotStats(context);
    if (!stats) return null;

    return (
      <div className={styles.tooltip_container}>
        {/* Measure Info */}
        <div className={styles.tooltip_section}>
          <h4 className={styles.tooltip_header}>Measure Information</h4>
          <div className={styles.tooltip_section}>
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>Measure</div>
              <div className={styles.tooltip_value}>
                {formatTooltipLabel(stats.measureKey, context)}
              </div>
            </div>
            {stats.type === "pivot" && (
              <>
                <div className={styles.tooltip_row}>
                  <div className={styles.tooltip_label}>Raw Value</div>
                  <div className={styles.tooltip_value}>
                    {isEmptyValue(stats.currentValue)
                      ? "No value (null)"
                      : String(stats.currentValue)}
                  </div>
                </div>
                <div className={styles.tooltip_row}>
                  <div className={styles.tooltip_label}>Formatted Value</div>
                  <div className={styles.tooltip_value}>
                    {formatTooltipValue(
                      stats.currentValue,
                      context.currentColumn?.field,
                      context
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Distribution for pivot cells */}
        {stats.type === "pivot" && stats.distribution.length > 0 && (
          <div className={styles.tooltip_section}>
            <h4 className={styles.tooltip_header}>Distribution</h4>
            <div className={styles.tooltip_section}>
              {stats.distribution.map((item) => (
                <div key={item.key} className={styles.tooltip_row}>
                  <div className={styles.tooltip_label}>
                    {formatTooltipLabel(item.key, context)}
                  </div>
                  <div className={styles.tooltip_bar}>
                    <div
                      className={styles.barFill}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                  <div className={styles.tooltip_value}>
                    {formatTooltipValue(
                      item.value,
                      context.currentColumn?.field,
                      context
                    )}
                  </div>
                  <div className={styles.tooltip_value}>
                    {item.percentage.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Aggregation stats */}
        {stats.type === "aggregation" &&
          Object.keys(stats.stats).length > 0 && (
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Aggregation Statistics</h4>
              <div className={styles.aggregationStats}>
                {Object.entries(stats.stats).map(([key, value]) => (
                  <div key={key} className={styles.infoRow}>
                    <span className={styles.label}>
                      {formatTooltipLabel(key, context)}
                    </span>
                    <span className={styles.value}>
                      {formatTooltipValue(value, key, context)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>
    );
  },
};
